import React from "react";
import {
  Box,
  Divider,
  Button,
  Grid,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import DynamicForm from "Components/DynamicForm";
import "./../Agreement/Formpage.scss";
import Swal from "sweetalert2";
import { getRequest } from "Includes/api/apicall";
import { GET_URL } from "Includes/urls";
import { useNavigate, useLocation } from "react-router-dom";

const DIGITAL_PAGE_SIZE = 40;

const DRAFT_KEY = "quotation_add_draft_v1";

const initFormData = () => ({
  schoolName: "",
  schoolAddress: "",
  peramount: "",
  erpAmountType: "per_student",
  erpWithGst: false,
  erpGstPercent: "18",
  notes: "",
  perId: "",
  biometric_auto_amount: "",
  biometric_auto_renewal: "",
  biometric_manual_amount: "",
  biometric_manual_renewal: "",
});

const SectionHeading = ({ children }) => (
  <Typography variant="subtitle1" sx={{ mb: 1.5, color: "#0a0a0a", fontWeight: 700 }}>
    {children}
  </Typography>
);

const featureByIdMap = (features) => {
  const m = new Map();
  (features || []).forEach((f) => m.set(Number(f.id), f));
  return m;
};

export default function QuotationAdd(props) {
  const history = useNavigate();
  const location = useLocation();
  const visitContext = location.state?.visit || null;
  const readDraft = React.useCallback(() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (e) {
      return null;
    }
  }, []);

  const draft = React.useMemo(() => readDraft(), [readDraft]);
  const [dynamicFormKey, setDynamicFormKey] = React.useState(0);
  const [initialValues, setInitialValues] = React.useState(() => ({
    ...initFormData(),
    ...(draft?.formData || {}),
    ...(visitContext
      ? {
          schoolName: visitContext.schoolName || "",
          schoolAddress: visitContext.schoolAddress || "",
          notes: visitContext.notes || "",
        }
      : {}),
  }));
  const [quotationPlanList, setQuotationPlanList] = React.useState([]);
  const [selectedPlanId, setSelectedPlanId] = React.useState(() => String(draft?.selectedPlanId || ""));
  const [selectedPlan, setSelectedPlan] = React.useState(null);
  const [formData, setFormData] = React.useState(() => ({
    ...initFormData(),
    ...(draft?.formData || {}),
    ...(visitContext
      ? {
          schoolName: visitContext.schoolName || "",
          schoolAddress: visitContext.schoolAddress || "",
          notes: visitContext.notes || "",
        }
      : {}),
  }));
  const [biometricIncludeAuto, setBiometricIncludeAuto] = React.useState(() => !!draft?.biometricIncludeAuto);
  const [biometricIncludeManual, setBiometricIncludeManual] = React.useState(() => !!draft?.biometricIncludeManual);

  const [serviceToggles, setServiceToggles] = React.useState({
    erp: !!draft?.serviceToggles?.erp,
    otherService: !!draft?.serviceToggles?.otherService,
    idCard: !!draft?.serviceToggles?.idCard,
    biometric: !!draft?.serviceToggles?.biometric,
  });

  const [ERPFeatureList, setERPFeatureList] = React.useState([]);
  const [selectedErpFeatures, setSelectedErpFeatures] = React.useState(() =>
    Array.isArray(draft?.selectedErpFeatures) ? draft.selectedErpFeatures.map(String) : []
  );

  const [digitalOptions, setDigitalOptions] = React.useState([]);
  const [digitalTotalCount, setDigitalTotalCount] = React.useState(0);
  const [digitalNextPage, setDigitalNextPage] = React.useState(1);
  const [digitalLoading, setDigitalLoading] = React.useState(false);
  const digitalLoadGuard = React.useRef(false);
  const [selectedDigitalFeatures, setSelectedDigitalFeatures] = React.useState([]);
  /** { [id]: row } */
  const [digitalFeatureSelection, setDigitalFeatureSelection] = React.useState(() =>
    draft?.digitalFeatureSelection && typeof draft.digitalFeatureSelection === "object"
      ? draft.digitalFeatureSelection
      : {}
  );

  const erpCategoriesEntries = React.useMemo(() => {
    const grouped = (ERPFeatureList || []).reduce((acc, item) => {
      const cat = (item.category || "").trim() || "General";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [ERPFeatureList]);

  const erpCategoryRows = React.useMemo(() => {
    const rows = [];
    const size = 3;
    for (let i = 0; i < erpCategoriesEntries.length; i += size) {
      rows.push(erpCategoriesEntries.slice(i, i + size));
    }
    return rows;
  }, [erpCategoriesEntries]);

  const allErpFeatureIds = React.useMemo(
    () => (ERPFeatureList || []).map((f) => String(f.id)),
    [ERPFeatureList]
  );

  const erpAllModulesSelected =
    allErpFeatureIds.length > 0 &&
    allErpFeatureIds.every((id) => selectedErpFeatures.includes(id));
  const erpModulesPartiallySelected =
    selectedErpFeatures.some((id) => allErpFeatureIds.includes(id)) && !erpAllModulesSelected;

  const metaById = React.useMemo(() => featureByIdMap(digitalOptions), [digitalOptions]);

  React.useEffect(() => {
    getRequest(GET_URL.quotation_plans.api, {}, props)
      .then((response) => {
        if (response && response.status === 200) {
          setQuotationPlanList(response.data.data || []);
        }
      })
      .catch(() => {});
  }, [props]);

  // Resolve selected plan object once list is loaded (draft restore)
  React.useEffect(() => {
    if (!selectedPlanId) {
      setSelectedPlan(null);
      return;
    }
    const plan = (quotationPlanList || []).find((p) => String(p.id) === String(selectedPlanId));
    setSelectedPlan(plan || null);
  }, [quotationPlanList, selectedPlanId]);

  React.useEffect(() => {
    if (!serviceToggles.erp) return;
    getRequest(GET_URL.erpfeature.api, { limit: 500, pageno: 1 }, props)
      .then((response) => {
        if (response?.status === 200) {
          setERPFeatureList(response.data?.data || []);
        }
      })
      .catch(() => {});
  }, [serviceToggles.erp, props]);

  const fetchDigitalPage = React.useCallback(
    (pageNo, append) => {
      if (digitalLoadGuard.current) return Promise.resolve();
      digitalLoadGuard.current = true;
      setDigitalLoading(true);
      return getRequest(
        GET_URL.digitalmarketingfeature?.api,
        { limit: DIGITAL_PAGE_SIZE, pageno: pageNo },
        props
      )
        .then((response) => {
          if (!response || response.status !== 200) return;
          const rows = response.data?.data || [];
          const count =
            typeof response.data?.count === "number" ? response.data.count : rows.length;
          setDigitalTotalCount(count);
          setDigitalNextPage(pageNo + 1);
          setDigitalOptions((prev) => {
            const base = append ? [...prev] : [];
            const seen = new Set(base.map((r) => r.id));
            rows.forEach((r) => {
              if (!seen.has(r.id)) {
                seen.add(r.id);
                base.push(r);
              }
            });
            return base;
          });
        })
        .catch(() => {})
        .finally(() => {
          digitalLoadGuard.current = false;
          setDigitalLoading(false);
        });
    },
    [props]
  );

  React.useEffect(() => {
    if (!serviceToggles.otherService) return;
    if (digitalOptions.length === 0 && !digitalLoading) fetchDigitalPage(1, false);
  }, [serviceToggles.otherService, digitalOptions.length, digitalLoading, fetchDigitalPage]);

  // Persist draft for back-navigation from preview
  React.useEffect(() => {
    try {
      const snapshot = {
        formData,
        selectedPlanId,
        serviceToggles,
        selectedErpFeatures,
        digitalFeatureSelection,
        biometricIncludeAuto,
        biometricIncludeManual,
      };
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(snapshot));
    } catch (e) {
      // ignore storage failures
    }
  }, [
    formData,
    selectedPlanId,
    serviceToggles,
    selectedErpFeatures,
    digitalFeatureSelection,
    biometricIncludeAuto,
    biometricIncludeManual,
  ]);

  const handleDigitalListScroll = (e) => {
    const list = e.currentTarget;
    if (!list || digitalLoading) return;
    if (digitalOptions.length >= digitalTotalCount && digitalTotalCount > 0) return;
    const nearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 64;
    if (nearBottom) fetchDigitalPage(digitalNextPage, true);
  };

  const updateParent = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = (key) => (event) => {
    const checked = event.target.checked;
    setServiceToggles((prev) => ({ ...prev, [key]: checked }));
    if (key === "erp" && !checked) setSelectedErpFeatures([]);
    if (key === "otherService" && !checked) {
      setSelectedDigitalFeatures([]);
      setDigitalFeatureSelection({});
      setDigitalOptions([]);
      setDigitalTotalCount(0);
      setDigitalNextPage(1);
    }
    if (key === "biometric" && !checked) {
      setBiometricIncludeAuto(false);
      setBiometricIncludeManual(false);
      setFormData((prev) => ({
        ...prev,
        biometric_auto_amount: "",
        biometric_auto_renewal: "",
        biometric_manual_amount: "",
        biometric_manual_renewal: "",
      }));
    }
  };

  const handlePlanChange = (planId) => {
    const normalizedPlanId = planId ? String(planId) : "";
    setSelectedPlanId(normalizedPlanId);
    const plan = quotationPlanList.find((p) => String(p.id) === normalizedPlanId);
    setSelectedPlan(plan || null);
    if (!plan) return;

    setFormData((prev) => ({
      ...prev,
      peramount:
        plan.amount_per_student != null && plan.amount_per_student !== ""
          ? String(plan.amount_per_student)
          : prev.peramount,
      erpWithGst: !!plan.erp_with_gst,
      erpGstPercent:
        plan.erp_gst_percent != null && plan.erp_gst_percent !== ""
          ? String(plan.erp_gst_percent)
          : prev.erpGstPercent,
      perId: plan.per_id_amount != null && plan.per_id_amount !== "" ? String(plan.per_id_amount) : prev.perId,
      biometric_auto_amount:
        plan.biometric_auto_amount != null && plan.biometric_auto_amount !== ""
          ? String(plan.biometric_auto_amount)
          : prev.biometric_auto_amount,
      biometric_auto_renewal:
        plan.biometric_auto_renewal != null && plan.biometric_auto_renewal !== ""
          ? String(plan.biometric_auto_renewal)
          : prev.biometric_auto_renewal,
      biometric_manual_amount:
        plan.biometric_manual_amount != null && plan.biometric_manual_amount !== ""
          ? String(plan.biometric_manual_amount)
          : prev.biometric_manual_amount,
      biometric_manual_renewal:
        plan.biometric_manual_renewal != null && plan.biometric_manual_renewal !== ""
          ? String(plan.biometric_manual_renewal)
          : prev.biometric_manual_renewal,
    }));
    setInitialValues((prev) => ({
      ...prev,
      peramount:
        plan.amount_per_student != null && plan.amount_per_student !== ""
          ? String(plan.amount_per_student)
          : prev.peramount,
    }));
    setServiceToggles({
      erp: !!plan.service_toggles?.erp,
      otherService: !!plan.service_toggles?.otherService,
      idCard: !!plan.service_toggles?.idCard,
      biometric: !!plan.service_toggles?.biometric,
    });
    setSelectedErpFeatures(Array.isArray(plan.erp_features) ? plan.erp_features.map(String) : []);
    setDigitalFeatureSelection(plan.digital_features || {});
    if (Array.isArray(plan.digital_feature_details)) {
      setSelectedDigitalFeatures(plan.digital_feature_details);
      setDigitalOptions((prev) => {
        const next = [...prev];
        const seen = new Set(next.map((item) => String(item.id)));
        plan.digital_feature_details.forEach((item) => {
          if (!seen.has(String(item.id))) {
            seen.add(String(item.id));
            next.push(item);
          }
        });
        return next;
      });
    } else {
      setSelectedDigitalFeatures([]);
    }
    setBiometricIncludeAuto(!!plan.biometric_include_auto);
    setBiometricIncludeManual(!!plan.biometric_include_manual);
    setDynamicFormKey((k) => k + 1);
  };

  const onDigitalSelectionChange = (_, newFeatures) => {
    setSelectedDigitalFeatures(newFeatures || []);
    setDigitalFeatureSelection((prev) => {
      const next = {};
      const selIds = new Set((newFeatures || []).map((f) => String(f.id)));
      (newFeatures || []).forEach((f) => {
        const idStr = String(f.id);
        next[idStr] =
          prev[idStr] ??
          ({
            price: f.price != null ? String(f.price) : "",
            renewal_price:
              f.renewal_price != null && f.renewal_price !== "" ? String(f.renewal_price) : "",
            calculation_type: f.calculation_type || "monthwise",
          });
      });
      for (const key of Object.keys(prev)) {
        if (!selIds.has(key)) delete next[key];
      }
      return next;
    });
  };

  const schoolFieldDetails = React.useMemo(
    () => [
      {
        label: "Institute Name",
        name: "schoolName",
        value: "schoolName",
        md: 6,
        className: "w-95-per",
        required: true,
        id: "quotation-school-name",
        default: initialValues.schoolName || "",
        rows: null,
        type: "text",
        maxLength: 100,
      },
      {
        label: "Institute Address",
        name: "schoolAddress",
        value: "schoolAddress",
        md: 12,
        required: true,
        id: "quotation-school-address",
        default: initialValues.schoolAddress || "",
        rows: 4,
        type: "text_area",
        maxLength: 500,
      },
    ],
    [initialValues.schoolAddress, initialValues.schoolName]
  );

  const validateQuotation = (fd, toggles) => {
    if (
      !toggles.erp &&
      !toggles.otherService &&
      !toggles.idCard &&
      !toggles.biometric
    ) {
      return "Select at least one service: ERP, Other service, ID card, or Biometric";
    }

    if (!fd.schoolName) return "Enter Institute Name";
    if (!fd.schoolAddress) return "Enter Institute Address";

    if (toggles.erp) {
      if (!selectedErpFeatures.length) return "Select at least one ERP module";
      const amountLabel = fd.erpAmountType === "fixed" ? "fixed amount" : "amount per student";
      if (!fd.peramount || String(fd.peramount).trim() === "") return `Enter ${amountLabel}`;
      const amt = Number(fd.peramount);
      if (!Number.isFinite(amt) || amt <= 0) return `ERP ${amountLabel} must be positive`;
      if (fd.erpWithGst) {
        if (
          fd.erpGstPercent === null ||
          fd.erpGstPercent === undefined ||
          String(fd.erpGstPercent).trim() === ""
        ) {
          return "Enter GST %";
        }
        const g = Number(fd.erpGstPercent);
        if (Number.isNaN(g) || g < 0 || g > 100) return "GST % must be between 0 and 100";
      }
    }

    if (toggles.otherService) {
      const ids = Object.keys(digitalFeatureSelection);
      if (!ids.length) return "Select at least one other service";
      for (const fid of ids) {
        const row = digitalFeatureSelection[fid];
        const meta = metaById.get(Number(fid));
        const label = meta?.name || `Other service #${fid}`;
        const priceEmpty = row?.price === "" || row?.price == null;
        if (priceEmpty || Number(row.price) < 0.01) {
          return `${label}: enter amount`;
        }
        if (
          !row?.calculation_type ||
          !["monthwise", "yearwise", "studentcount"].includes(row.calculation_type)
        ) {
          return `${label}: select pricing basis`;
        }
      }
    }

    if (toggles.idCard) {
      if (!fd.perId || Number(fd.perId) <= 0) return "Enter ID card amount";
    }

    if (toggles.biometric) {
      if (biometricIncludeAuto) {
        const au = Number(fd.biometric_auto_amount);
        if (!fd.biometric_auto_amount || !Number.isFinite(au) || au <= 0) {
          return "Enter biometric (auto) amount";
        }
      }
      if (biometricIncludeManual) {
        const mu = Number(fd.biometric_manual_amount);
        if (!fd.biometric_manual_amount || !Number.isFinite(mu) || mu <= 0) {
          return "Enter biometric (manual) amount";
        }
      }
    }

    let subtotal = 0;
    if (toggles.erp) {
      subtotal += Number(fd.peramount) || 0;
    }
    if (toggles.otherService) {
      for (const row of Object.values(digitalFeatureSelection)) {
        subtotal += Number(row.price) || 0;
      }
    }
    if (toggles.idCard) {
      subtotal += Number(fd.perId) || 0;
    }
    if (toggles.biometric) {
      if (biometricIncludeAuto && fd.biometric_auto_amount !== "") {
        subtotal += Number(fd.biometric_auto_amount) || 0;
      }
      if (biometricIncludeManual && fd.biometric_manual_amount !== "") {
        subtotal += Number(fd.biometric_manual_amount) || 0;
      }
    }

    if (!Number.isFinite(subtotal) || subtotal <= 0) return "Quotation total must be greater than zero";

    return "";
  };

  const buildLineItems = () => {
    const items = [];
    if (serviceToggles.erp) {
      items.push({
        item: "ERP subscription",
        plan: selectedPlan?.name || "—",
        service: selectedPlan?.description || "—",
        amount: Number(formData.peramount) || 0,
      });
    }
    if (serviceToggles.otherService) {
      for (const [fid, row] of Object.entries(digitalFeatureSelection)) {
        const basis = row.calculation_type || "monthwise";
        const basisLabel =
          basis === "yearwise" ? "Year wise" : basis === "studentcount" ? "Student count" : "Month wise";
        const rn = row.renewal_price != null && String(row.renewal_price).trim() !== "";
        items.push({
          item: "Other service",
          feature_id: Number(fid),
          plan: "—",
          service: `${basisLabel}${rn ? ` · Renewal ₹${Number(row.renewal_price).toFixed(2)}` : ""}`,
          amount: Number(row.price) || 0,
        });
      }
    }
    if (serviceToggles.idCard) {
      items.push({
        item: "ID card",
        plan: "—",
        service: "—",
        amount: Number(formData.perId) || 0,
      });
    }
    if (serviceToggles.biometric) {
      if (biometricIncludeAuto) {
        const au = Number(formData.biometric_auto_amount);
        items.push({
          item: "Biometric — Auto",
          plan: "—",
          service: formData.biometric_auto_renewal
            ? `Renewal ₹${Number(formData.biometric_auto_renewal).toFixed(2)}`
            : "—",
          amount: Number.isFinite(au) ? au : 0,
        });
      }
      if (biometricIncludeManual) {
        const mu = Number(formData.biometric_manual_amount);
        items.push({
          item: "Biometric — Manual",
          plan: "—",
          service: formData.biometric_manual_renewal
            ? `Renewal ₹${Number(formData.biometric_manual_renewal).toFixed(2)}`
            : "—",
          amount: Number.isFinite(mu) ? mu : 0,
        });
      }
    }
    return items;
  };

  const buildPreviewPayload = () => {
    const lineItems = buildLineItems();
    const subtotal = lineItems.reduce((s, r) => s + Number(r.amount || 0), 0);
    const gstPct =
      serviceToggles.erp && formData.erpWithGst ? Number(formData.erpGstPercent) || 0 : 0;
    const erpAmountCriteria = formData.erpAmountType === "fixed" ? "Fixed amount" : "Amount per student";
    return {
      lineItems,
      amount: subtotal,
      gst: gstPct,
      company_name: formData.schoolName,
      company_address: formData.schoolAddress,
      contact_person: "",
      contact_phone: visitContext?.contactPhone || "",
      valid_until: "",
      notes: formData.notes || "",
      plan_name: selectedPlan?.name || "",
      service_name: selectedPlan?.description || visitContext?.notes || "",
      sales_meta: {
        visit_id: visitContext?.visitId || null,
        quotation_status: "generated",
        erpModules: [...selectedErpFeatures],
        erpWithGst: !!formData.erpWithGst,
        erpAmountCriteria,
      },
      salesMeta: {
        erpModules: [...selectedErpFeatures],
        erpWithGst: !!formData.erpWithGst,
        erpAmountCriteria,
      },
    };
  };

  const handleGenerateQuotation = (e) => {
    if (e?.preventDefault) e.preventDefault();
    const validationError = validateQuotation(formData, serviceToggles);
    if (validationError) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: validationError,
      });
      return;
    }
    history("/quotation/preview", { state: { data: buildPreviewPayload() } });
  };

  const setDigitalRow = (idStr, patch) => {
    setDigitalFeatureSelection((prev) => ({
      ...prev,
      [idStr]: { ...(prev[idStr] || {}), ...patch },
    }));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%", bgcolor: "background.default" }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: "#0a0a0a" }}>
          Quotation
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
          Enter details required for the quotation printout
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Paper elevation={0} variant="outlined" sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
          <Typography variant="subtitle2" sx={{ color: "#0a0a0a", fontWeight: 700, mb: 1 }}>
            Quotation plan <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
          </Typography>
          <TextField
            select
            fullWidth
            label="Choose plan (package)"
            value={selectedPlanId}
            onChange={(e) => handlePlanChange(e.target.value)}
            size="small"
            helperText="Optional - prefills saved quotation amounts, services, and modules."
          >
            <MenuItem value="">— No plan —</MenuItem>
            {quotationPlanList.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.name}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        <Paper elevation={0} variant="outlined" sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#0a0a0a" }}>
            Services to include
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={<Checkbox checked={serviceToggles.erp} onChange={handleToggle("erp")} />}
                label="ERP"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox checked={serviceToggles.otherService} onChange={handleToggle("otherService")} />
                }
                label="Other service"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={<Checkbox checked={serviceToggles.idCard} onChange={handleToggle("idCard")} />}
                label="ID card"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox checked={serviceToggles.biometric} onChange={handleToggle("biometric")} />
                }
                label="Biometric"
              />
            </Grid>
          </Grid>
        </Paper>

        <SectionHeading>Institute details</SectionHeading>
        <DynamicForm
          key={`quotation-school-${dynamicFormKey}`}
          fieldDetails={schoolFieldDetails}
          updateParent={updateParent}
          loading={false}
        />

        {serviceToggles.erp && (
          <>
            <Box mt={3} mb={3}>
              <Divider />
            </Box>
            <SectionHeading>Subscription pricing</SectionHeading>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={formData.erpAmountType === "fixed" ? "fixed" : "per_student"}
                  onChange={(_, v) => {
                    if (v != null) updateParent("erpAmountType", v);
                  }}
                >
                  <ToggleButton value="per_student" sx={{ textTransform: "none", px: 2 }}>
                    Per student
                  </ToggleButton>
                  <ToggleButton value="fixed" sx={{ textTransform: "none", px: 2 }}>
                    Fixed amount
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  id="quotation-amount-per-student"
                  label={formData.erpAmountType === "fixed" ? "Fixed amount" : "Amount per student"}
                  type="number"
                  size="small"
                  fullWidth
                  required
                  value={formData.peramount ?? ""}
                  onChange={(e) => updateParent("peramount", e.target.value)}
                  inputProps={{ min: 0.01, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={formData.erpWithGst ? "with" : "without"}
                  onChange={(_, v) => {
                    if (v != null) updateParent("erpWithGst", v === "with");
                  }}
                >
                  <ToggleButton value="without" sx={{ textTransform: "none", px: 2 }}>
                    Without GST
                  </ToggleButton>
                  <ToggleButton value="with" sx={{ textTransform: "none", px: 2 }}>
                    With GST
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              {formData.erpWithGst && (
                <Grid item xs={12} md={4}>
                  <TextField
                    id="quotation-erp-gst-pct"
                    label="GST %"
                    type="number"
                    size="small"
                    fullWidth
                    required
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    value={formData.erpGstPercent ?? ""}
                    onChange={(e) => updateParent("erpGstPercent", e.target.value)}
                  />
                </Grid>
              )}
            </Grid>

            <Box mt={3} mb={2}>
              <Divider />
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 1.5 }}>
              <Checkbox
                size="small"
                sx={{ mt: -0.25, p: 0.5 }}
                disabled={allErpFeatureIds.length === 0}
                checked={erpAllModulesSelected}
                indeterminate={erpModulesPartiallySelected}
                onChange={(ev) => {
                  if (ev.target.checked) setSelectedErpFeatures([...allErpFeatureIds]);
                  else setSelectedErpFeatures([]);
                }}
                inputProps={{ "aria-label": "Select all ERP modules" }}
              />
              <Typography variant="subtitle1" sx={{ color: "#0a0a0a", fontWeight: 700, pt: 0.25 }}>
                ERP modules
              </Typography>
            </Box>
            {erpCategoryRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Loading modules… or none available.
              </Typography>
            ) : (
              erpCategoryRows.map((row, rowIdx) => (
                <Grid container spacing={2} key={`erp-row-${rowIdx}`} sx={{ mb: 2 }}>
                  {row.map(([category, items]) => {
                    const categoryIds = items.map((f) => String(f.id));
                    const selectedInCategory = categoryIds.filter((id) => selectedErpFeatures.includes(id))
                      .length;
                    const allSelected = categoryIds.length > 0 && selectedInCategory === categoryIds.length;
                    const partiallySelected = selectedInCategory > 0 && !allSelected;
                    return (
                      <Grid item xs={12} md={4} key={category}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            height: "100%",
                            minHeight: 180,
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                              pb: 0.75,
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={700}>
                              {category}
                            </Typography>
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={allSelected}
                                  indeterminate={partiallySelected}
                                  onChange={(ev) => {
                                    setSelectedErpFeatures((prev) => {
                                      const prevSet = new Set(prev);
                                      if (ev.target.checked)
                                        categoryIds.forEach((id) => prevSet.add(id));
                                      else categoryIds.forEach((id) => prevSet.delete(id));
                                      return Array.from(prevSet);
                                    });
                                  }}
                                />
                              }
                              label={<Typography variant="caption">All</Typography>}
                            />
                          </Box>
                          <Box sx={{ flex: 1, overflowY: "auto", maxHeight: 280 }}>
                            {items.map((feature) => (
                              <FormControlLabel
                                key={feature.id}
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  ml: 0,
                                  mb: 0.5,
                                  "& .MuiFormControlLabel-label": { mt: 0.25 },
                                }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={selectedErpFeatures.includes(String(feature.id))}
                                    onChange={(ev) => {
                                      const idStr = String(feature.id);
                                      if (ev.target.checked)
                                        setSelectedErpFeatures((prev) =>
                                          prev.includes(idStr) ? prev : [...prev, idStr]
                                        );
                                      else
                                        setSelectedErpFeatures((prev) =>
                                          prev.filter((x) => x !== idStr)
                                        );
                                    }}
                                  />
                                }
                                label={
                                  <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                                    {feature.name}
                                  </Typography>
                                }
                              />
                            ))}
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              ))
            )}
          </>
        )}

        {serviceToggles.otherService && (
          <>
            <Box mt={3} mb={3}>
              <Divider />
            </Box>
            <SectionHeading>Other services</SectionHeading>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
              Search and scroll the list — more rows load automatically.
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              loading={digitalLoading && digitalOptions.length === 0}
              options={digitalOptions}
              value={selectedDigitalFeatures}
              onChange={onDigitalSelectionChange}
              getOptionLabel={(o) => (o && o.name ? o.name : "")}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              ListboxProps={{ onScroll: handleDigitalListScroll, style: { maxHeight: 280 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select other services"
                  size="small"
                  placeholder={digitalLoading ? "Loading…" : "Type to filter"}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {digitalLoading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {Object.keys(digitalFeatureSelection).map((fid) => {
              const idStr = String(fid);
              const row = digitalFeatureSelection[idStr] || {};
              const meta = metaById.get(Number(fid));
              const label = meta?.name || idStr;
              const calculationType = row.calculation_type || "monthwise";
              return (
                <Paper key={idStr} variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                    {label}
                  </Typography>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Amount"
                        type="number"
                        size="small"
                        fullWidth
                        required
                        value={row.price ?? ""}
                        onChange={(e) => setDigitalRow(idStr, { price: e.target.value })}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Renewal amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={row.renewal_price ?? ""}
                        onChange={(e) => setDigitalRow(idStr, { renewal_price: e.target.value })}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        label="Pricing basis"
                        size="small"
                        fullWidth
                        value={calculationType}
                        onChange={(e) => setDigitalRow(idStr, { calculation_type: e.target.value })}
                      >
                        <MenuItem value="monthwise">Month wise</MenuItem>
                        <MenuItem value="yearwise">Year wise</MenuItem>
                        <MenuItem value="studentcount">Student count</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </>
        )}

        {serviceToggles.idCard && (
          <>
            <Box mt={3} mb={3}>
              <Divider />
            </Box>
            <SectionHeading>ID card</SectionHeading>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Amount"
                  name="perId"
                  type="number"
                  value={formData.perId || ""}
                  onChange={(e) => updateParent("perId", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0.01, step: 0.01 }}
                />
              </Grid>
            </Grid>
          </>
        )}

        {serviceToggles.biometric && (
          <>
            <Box mt={3} mb={3}>
              <Divider />
            </Box>
            <SectionHeading>Biometric</SectionHeading>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={biometricIncludeAuto}
                      onChange={(e) => {
                        const c = e.target.checked;
                        setBiometricIncludeAuto(c);
                        if (!c) {
                          setFormData((prev) => ({
                            ...prev,
                            biometric_auto_amount: "",
                            biometric_auto_renewal: "",
                          }));
                        }
                      }}
                    />
                  }
                  label="Auto biometric"
                />
                {biometricIncludeAuto && (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={6}>
                      <TextField
                        label="Amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={formData.biometric_auto_amount}
                        onChange={(e) => updateParent("biometric_auto_amount", e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Renewal amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={formData.biometric_auto_renewal}
                        onChange={(e) => updateParent("biometric_auto_renewal", e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={biometricIncludeManual}
                      onChange={(e) => {
                        const c = e.target.checked;
                        setBiometricIncludeManual(c);
                        if (!c) {
                          setFormData((prev) => ({
                            ...prev,
                            biometric_manual_amount: "",
                            biometric_manual_renewal: "",
                          }));
                        }
                      }}
                    />
                  }
                  label="Manual biometric"
                />
                {biometricIncludeManual && (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={6}>
                      <TextField
                        label="Amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={formData.biometric_manual_amount}
                        onChange={(e) => updateParent("biometric_manual_amount", e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Renewal amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={formData.biometric_manual_renewal}
                        onChange={(e) => updateParent("biometric_manual_renewal", e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </>
        )}

        <Box mt={3} mb={2}>
          <Divider />
        </Box>

        <Box mt={2} mb={1}>
          <TextField
            id="quotation-notes"
            label="Notes (optional)"
            fullWidth
            multiline
            minRows={2}
            value={formData.notes ?? ""}
            onChange={(e) => updateParent("notes", e.target.value)}
            size="small"
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={handleGenerateQuotation}
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            Save
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
